import { Field, WysiwygField } from '../../../libs/gs-component-form';
import { Content } from '../../../libs/gs-component-right-rail';
import { FormBlockColumn, FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import { Field as FormikField } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Dictionary } from '../../../helpers/filter';
import { SharedFormProps } from '../../../helpers/useManageItem';
import { Footer } from '../../FormComponents';
import { DeleteOrDiscard } from '../DeleteOrDiscard';
import { ImageField } from '../ImageField';
import PlaceTypeahead, { PlaceTypeaheadResponse } from '../PlaceTypeahead';
import { PhotoValues } from '../StoryItem/Form';
import SubtleHeading from '../SubtleHeading';
import styles from './styles.scss';
import { useAiTextExperimentContext } from '../../../context/AiTextExperimentContext';

const FIELD_NAMES = {
  NAME: 'name',
  ADDRESS: 'address',
  PHONE: 'phone',
  WEBSITE: 'website',
};

export const validationSchema = Yup.object().shape({
  address: Yup.string(),
  description: Yup.string(),
  email: Yup.string(),
  name: Yup.string().required('Name cannot be blank'),
  phone: Yup.string(),
  website: Yup.string(),
  checkInDate: Yup.string()
    .trim()
    .matches(/\d{4}-\d{2}-\d{2}/),
  checkOutDate: Yup.string()
    .trim()
    .matches(/\d{4}-\d{2}-\d{2}/),
  guestCode: Yup.string(),
  rate: Yup.string(),
});

export interface Attributes {
  address?: string;
  description?: string;
  email?: string;
  name: string;
  pageId: number;
  phone?: string;
  website?: string;
  checkInDate?: string;
  checkOutDate?: string;
  guestCode?: string;
  rate?: string;
}

export interface InitialValues extends Dictionary {
  address: string;
  description: string;
  email: string;
  name: string;
  phone: string;
  website: string;
  checkInDate: string;
  checkOutDate: string;
  guestCode: string;
  rate: string;
  photo: PhotoValues;
}

const EDITOR_MODULES = [['bold', 'italic'], ['link'], [{ align: [] }]];

const EDITOR_FORMATS: string[] = ['bold', 'italic', 'link', 'align'];

export type BusinessItem = {
  id: number;
  address?: string | null;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  name?: string | null;
  checkInDate?: string | null;
  checkOutDate?: string | null;
  guestCode?: string | null;
  rate?: string | null;
  photo: PhotoValues | null;
};

interface Props extends SharedFormProps {
  item?: BusinessItem | null;
  children?: React.ReactNode | React.ReactNode[] | null;
  pageId: number;
}

const initialPhoto: PhotoValues = {
  mediaUrl: null,
  mediaApiId: null,
  width: null,
  height: null,
  cropX: null,
  cropY: null,
  rotation: null,
};

export function initialValues<T extends any>(item?: T | null): InitialValues {
  return {
    address: (item as BusinessItem)?.address || '',
    description: (item as BusinessItem)?.description || '',
    email: (item as BusinessItem)?.email || '',
    name: (item as BusinessItem)?.name || '',
    phone: (item as BusinessItem)?.phone || '',
    website: (item as BusinessItem)?.website || '',
    checkInDate: (item as BusinessItem)?.checkInDate || '',
    checkOutDate: (item as BusinessItem)?.checkOutDate || '',
    guestCode: (item as BusinessItem)?.guestCode || '',
    rate: (item as BusinessItem)?.rate || '',
    photo: (item as BusinessItem)?.photo || initialPhoto,
  };
}

const Form: React.FC<Props> = ({
  submitForm,
  setFieldValue,
  loading,
  updating,
  children,
  pageId,
  isNew,
  discardOrDelete,
  close,
  hiddenFields,
}) => {
  const { showAiButton } = useAiTextExperimentContext();
  return (
    <>
      <Content className={`${styles.content} transportation-item`} loading={loading}>
        <FormBlockColumn style={{ zIndex: 19 }}>
          <FormikField
            name={FIELD_NAMES.NAME}
            label="Name (Required)"
            onChange={(values: PlaceTypeaheadResponse) => {
              setFieldValue(FIELD_NAMES.NAME, values.name);
              setFieldValue(FIELD_NAMES.ADDRESS, values.formatted_address);
              setFieldValue(FIELD_NAMES.PHONE, values.formatted_phone_number);
              setFieldValue(FIELD_NAMES.WEBSITE, values.website);
            }}
            data-testid="name"
            component={PlaceTypeahead}
          />
        </FormBlockColumn>
        <FormBlockColumn>
          <div className={styles.imageBox}>
            <FormikField
              component={ImageField}
              name="photo.mediaUrl"
              pageId={pageId}
              parentItemType="BusinessItem"
              imageFieldName="photo"
              imageTestId="business-item-upload"
            />
          </div>
        </FormBlockColumn>
        {!hiddenFields?.includes(FIELD_NAMES.ADDRESS) && (
          <FormBlockColumn>
            <FormikField data-testid="address" name={FIELD_NAMES.ADDRESS} label="Address" component={Field} />
          </FormBlockColumn>
        )}
        <FormRow>
          {!hiddenFields?.includes(FIELD_NAMES.PHONE) && (
            <FormColumn xs="12" md>
              <FormikField data-testid="phone" name={FIELD_NAMES.PHONE} label="Phone Number" component={Field} />
            </FormColumn>
          )}
          {!hiddenFields?.includes('email') && (
            <FormColumn xs="12" md>
              <FormikField data-testid="email" name="email" label="Email Address" component={Field} />
            </FormColumn>
          )}
        </FormRow>
        {!hiddenFields?.includes(FIELD_NAMES.WEBSITE) && (
          <FormBlockColumn>
            <FormikField data-testid="website" name={FIELD_NAMES.WEBSITE} label="Website" component={Field} />
          </FormBlockColumn>
        )}
        {children}
        <SubtleHeading>Details</SubtleHeading>
        <FormBlockColumn>
          <FormikField
            name="description"
            placeholder="What should guests know before they book this?"
            modules={EDITOR_MODULES}
            formats={EDITOR_FORMATS}
            component={WysiwygField}
            showAiButton={showAiButton}
            bounds=".transportation-item"
          />
        </FormBlockColumn>
        <DeleteOrDiscard isNew={isNew} onClick={discardOrDelete}>
          Delete item
        </DeleteOrDiscard>
      </Content>
      <Footer
        primaryOnClick={submitForm}
        secondaryOnClick={close}
        showSpinner={loading || updating}
        useSaveAndPreview={true}
      />
    </>
  );
};

export default Form;
